'use client';

import * as React from 'react';
import { useState } from 'react';
import type { Option } from '@/app/types';
import { cn } from '@/app/utils';
import { CheckboxFill } from './checkbox-fill';
import { Select, SelectContent, SelectTrigger } from './select';
import { toast } from './use-toast';

export interface MultiSelectProps {
  options: Option<string>[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  maxCount?: number;
  maxCountMessage?: string;
}

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      value = [],
      onChange,
      placeholder = '请选择',
      className,
      maxCount,
      maxCountMessage = '已达到最大选择数量',
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);

    // 处理选项选中/取消选中
    const handleSelect = (optionValue: string) => {
      const newValue = [...value];
      const index = newValue.indexOf(optionValue);

      // 如果是取消选中，允许操作
      if (index > -1) {
        newValue.splice(index, 1);
        onChange(newValue);
        return;
      }

      // 如果设置了最大选择数量且已经达到上限，显示提示并阻止操作
      if (maxCountMessage && maxCount !== undefined && newValue.length >= maxCount) {
        toast({
          type: 'custom',
          message: maxCountMessage,
        });
        return;
      }

      newValue.push(optionValue);
      onChange(newValue);
    };

    // 显示已选的值
    const getDisplayValue = () => {
      if (value.length === 0) {
        return placeholder;
      }

      const selectedLabels = value.map((v) => {
        const option = options.find((opt) => opt.value === v);
        // 兼容 options 改变，导致 value 不在范围内的情况，直接显示 value
        return option ? option.label : v;
      });
      return selectedLabels.join(',');
    };

    return (
      <Select
        open={open}
        onOpenChange={setOpen}
        onValueChange={(v) => {
          handleSelect(v);
        }}
      >
        <SelectTrigger
          ref={ref}
          className={cn(
            'flex h-fit items-center justify-between rounded-8 border-none p-3 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <div className={cn('line-clamp-1 flex-1', value.length === 0 && 'text-system-white36')}>
            {getDisplayValue()}
          </div>
        </SelectTrigger>
        <SelectContent
          className="max-h-60 w-[var(--radix-select-trigger-width)] overflow-y-auto p-1"
          position="popper"
          align="start"
          scrollUpIcon={false}
          scrollDownIcon={false}
        >
          <div className="flex flex-col gap-0.5">
            {options.map((option) => {
              // 判断是否选中
              const isSelected = value.includes(option.value);
              // 判断是否达到最大选择数量
              const isDisabled = !maxCountMessage && maxCount !== undefined && value.length >= maxCount && !isSelected;

              return (
                <div
                  key={option.value}
                  className={cn(
                    'flex items-center gap-2 rounded-4 p-2 text-sm',
                    isSelected && 'bg-system-hover01',
                    isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-system-hover01',
                  )}
                  onClick={() => !isDisabled && handleSelect(option.value)}
                >
                  <CheckboxFill checked={isSelected} className="text-xl" disabled={isDisabled} />
                  <div className="line-clamp-1 flex-1">{option.label}</div>
                </div>
              );
            })}
          </div>
        </SelectContent>
      </Select>
    );
  },
);

MultiSelect.displayName = 'MultiSelect';
